/**
 * Fast client-side image compression using the browser's own canvas —
 * avoids shipping 5-12 MB camera photos over the network.
 *
 * Typical result: a 4000×3000 px phone photo (~5 MB HEIC/JPEG) becomes a
 * 1920×1440 px JPEG (~250-400 KB) in ~80-200 ms on a mid-range phone.
 *
 * Falls back to the original file if anything goes wrong (non-image, huge
 * image that exceeds canvas limits, decode failure, etc.) so upload never
 * breaks.
 */
export async function compressImage(
  file,
  { maxDimension = 1920, quality = 0.82, mimeType = "image/jpeg" } = {}
) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  // If already small, skip work.
  if (file.size <= 500 * 1024) return file;

  try {
    // Prefer createImageBitmap — it's ~2-5x faster than <img> decoding and
    // runs off the main thread where supported.
    const bitmap = await (window.createImageBitmap
      ? createImageBitmap(file)
      : loadImageFallback(file));

    const { width: srcW, height: srcH } = bitmap;
    const scale = Math.min(1, maxDimension / Math.max(srcW, srcH));
    const dstW = Math.round(srcW * scale);
    const dstH = Math.round(srcH * scale);

    // Use OffscreenCanvas if available (non-blocking on main thread), else canvas.
    const canvas =
      typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(dstW, dstH)
        : Object.assign(document.createElement("canvas"), { width: dstW, height: dstH });
    if (canvas.width !== dstW) canvas.width = dstW;
    if (canvas.height !== dstH) canvas.height = dstH;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, dstW, dstH);
    if (typeof bitmap.close === "function") bitmap.close();

    const blob = await (canvas.convertToBlob
      ? canvas.convertToBlob({ type: mimeType, quality })
      : new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality)));

    if (!blob || blob.size >= file.size) return file; // no win → keep original

    // Preserve original filename (but swap extension to .jpg)
    const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, {
      type: mimeType,
      lastModified: file.lastModified || Date.now(),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Image compression failed, using original:", err);
    return file;
  }
}

function loadImageFallback(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight,
        // Fake bitmap interface for ctx.drawImage (HTMLImageElement works too)
        close: () => {},
        // drawImage works with <img> directly:
        ...img,
        __rawImg: img });
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
